import Link from "@components/Link";
import DefaultThumbnail from "@src/app/assets/default-thumbnail.jpg";
import ArrowRight from "@src/app/assets/icons/arrow-right.svg?react";
import type { BFFArticleCard } from "@src/app/types/bff";
import cx from "classnames";
import { type ReactElement, useEffect, useRef, useState } from "react";

import Button from "../Button/Button";
import Byline from "../Byline/Byline";
import CategoryTag from "../CategoryTag";
import SubHeader from "../SubHeader/SubHeader";
// import TranslatorCard from "../TranslatorCard/TranslatorCard";

type Props = Readonly<{
  title: string;
  category: string;
  authors?: BFFArticleCard["authors"];
  // translator?: BFFArticleCard["translator"];
  description: string;
  urlPath: string;
  imageUrl?: string;
  imageAlt?: string;
  section?: string;
}>;

export default function EditorsPickCard({
  title,
  category,
  authors,
  // translator,
  description,
  urlPath,
  imageUrl,
  imageAlt,
  section,
}: Props): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // alert('visbile')
          observer.disconnect(); // only trigger once
        }
      },
      { threshold: 0.5 }, // element is 80% visible
    );
    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={ref} data-testid="editors-pick-card">
      <Link
        to={urlPath}
        className={cx(
          "block items-center justify-center lg:flex lg:min-h-200",
          "bg-cover bg-center bg-no-repeat lg:bg-(image:--editor-bg) lg:bg-fixed",
        )}
        style={
          {
            "--editor-bg": `url(${imageUrl || DefaultThumbnail})`,
          } as React.CSSProperties
        }
      >
        <SubHeader title="Editor's pick" noIcon className="lg:hidden" />

        {/* Mobile Image */}
        <img
          className="mb-4 block h-auto w-full lg:hidden"
          src={imageUrl || DefaultThumbnail}
          alt={imageAlt || ""}
        />

        <div className="flex flex-col gap-4 px-2 lg:max-w-229 lg:items-center lg:justify-center lg:px-0">
          <CategoryTag
            name={category}
            className={cx(
              "lg:opacity-0",
              isVisible ? "lg:animate-fade-in-up" : "",
            )}
            style={{
              animationDelay: `100ms`,
            }}
            href={`/${section}`}
          />

          <h1
            className={cx(
              "text-header-3 text-primary lg:text-header-1 lg:text-primary-on-dark lg:text-center lg:opacity-0 lg:[text-shadow:0px_4px_14px_rgba(0,0,0,0.45)]",
              isVisible ? "lg:animate-fade-in-up" : "",
            )}
            style={{
              animationDelay: `200ms`,
            }}
          >
            {title}
          </h1>

          <div
            className={cx(
              "bg-background-default lg:bg-background-tertiary grid gap-4 py-4 lg:px-6 lg:opacity-0",
              isVisible ? "lg:animate-fade-in-up" : "",
            )}
            style={{
              animationDelay: `300ms`,
            }}
          >
            <div className="grid gap-4 lg:grid-cols-3">
              {authors &&
                authors.length > 0 &&
                authors
                  .slice(0, 6)
                  .map((author) => (
                    <Byline key={author.id} author={author} size="small" />
                  ))}
            </div>

            {/* Translator Card */}
            {/* {translator && translator.length > 0 && (
              <TranslatorCard translator={translator} />
            )} */}

            <p className="text-body-2">{description}</p>

            <Link to={urlPath}>
              <Button
                variant="primary"
                size="medium"
                icon={<ArrowRight />}
                iconPosition="right"
                className="block lg:float-right"
              >
                Read More
              </Button>
            </Link>
          </div>
        </div>
      </Link>
    </div>
  );
}
